import { AlertChannel, AlertDigestMode, AlertSeverity, AlertSubscription, AlertSubscriptions } from "@app/shared";
import { FC } from "react";
import { toastError, toastSuccess } from "../../../helpers";
import { useConfirmDialog, useEditModelDialog, useFetch } from "../../../hooks";
import { IconLink, Icons, PageContainer, SectionIntro, SortableTable } from "../../shared";
import { DropDownMenu } from "../../shared/DropDownMenu";
import { CheckboxInput, EnumSelect, FlagEnumCheckboxInput, InputLabel, InputRow, Select } from "../../shared/inputs";
import { channelsLabel, digestModeLabel, enumLabel, minSeverityLabel, minSeverityOptions, zonedTime } from "./alertDisplay";
import { AlertScope, AlertScopeInputs, useAlertScopeOptions } from "./AlertScope";
import { ModelHelpers } from "@app/shared/src/hooks";

type AlertSubscriptionTableProps = {
    organizationId: string;
};

// A user's own alert subscriptions: which scope they want to hear about, from what
// severity up, over which channels. Webhook is deliberately not offered here —
// webhooks are org-level destinations configured on their own tab, not something a
// single user's subscription delivers to.
export const AlertSubscriptionTable: FC<AlertSubscriptionTableProps> = ({ organizationId }) => {
    const [subscriptions, , subscriptionHelpers] = useFetch(() => AlertSubscriptions.list(), []);
    const { scopeLabel } = useAlertScopeOptions(organizationId, true);
    const subscriptionDialog = useEditModelDialog(AlertSubscription);
    const confirmDialog = useConfirmDialog();

    // Errors are intentionally NOT caught here: useEditModelDialog runs the handler
    // through validateWithAction, which puts a failed save's message in the dialog's
    // error summary and leaves the dialog open with the user's input intact.
    const saveSubscription = async (toSave: Partial<AlertSubscription>) => {
        // Channels has no server-side minimum (0 is a legal value that simply notifies
        // nobody), so the dialog holds the line — returning true keeps it open.
        if (!toSave.channels) {
            subscriptionDialog.modelHelpers.setSummaryError(["Select at least one notification channel."]);
            return true;
        }

        if (toSave.id) await AlertSubscriptions.update(toSave.id, toSave as AlertSubscription);
        else await AlertSubscriptions.create(toSave as AlertSubscription);

        subscriptionHelpers.refreshData();
        toastSuccess("Subscription Saved", "You'll be notified for alerts matching this subscription.");
    };

    const handleAddOnClick = () => {
        subscriptionDialog.show(saveSubscription, "Add Subscription", {
            organizationId,
            minSeverity: AlertSeverity.Warning,
            channels: AlertChannel.Email | AlertChannel.InApp,
            digestMode: AlertDigestMode.Immediate,
            notifyOnAutoResolve: false,
        });
    };

    const handleEditOnClick = (toEdit: AlertSubscription | undefined) => {
        if (!toEdit) return;
        subscriptionDialog.show(saveSubscription, "Edit Subscription", toEdit);
    };

    const handleDeleteOnClick = (toDelete: AlertSubscription | undefined) => {
        if (!toDelete) return;
        confirmDialog.show("Remove Subscription?", "You will stop receiving notifications for this scope.", "Remove", async () => {
            try {
                await AlertSubscriptions.deleteSubscription(toDelete.id);
                subscriptionHelpers.refreshData();
                toastSuccess("Subscription Removed");
            } catch (e) {
                toastError("Error Removing Subscription", e + "");
            }
        });
    };

    // The three scope ids are mutually exclusive, so a scope change has to replace the
    // model rather than update one key at a time.
    const applyScope = (model: Partial<AlertSubscription>, helpers: ModelHelpers<AlertSubscription>, scope: AlertScope) => {
        const next = new AlertSubscription();
        Object.assign(next, model, { organizationId: undefined, facilityId: undefined, deviceId: undefined }, scope);
        helpers.replaceModel(next);
    };

    return (
        <>
            <PageContainer
                Icon={Icons.AlertTriangle}
                title="My Alert Subscriptions"
                titleRight={
                    <div className="flex justify-center items-center">
                        <IconLink Icon={Icons.PlusCircle} onClick={handleAddOnClick}>
                            Add New
                        </IconLink>
                    </div>
                }
            >
                <SectionIntro title="Where you personally get told about device problems.">
                    <p>
                        These settings apply to <strong>your login only</strong> — everyone in your organization manages their
                        own, and changing yours doesn't affect anyone else.
                    </p>
                    <p>
                        Pick what to watch (the whole organization, one location, or a single device), how serious a problem has
                        to be before you hear about it, and how you'd like to be told: email to your account address, or the
                        alert bell in this dashboard. <strong>Immediate</strong> tells you as soon as something opens, when
                        someone acknowledges it, and when a person resolves it; <strong>Daily digest</strong> replaces all of
                        that with one summary email each morning. An alert that clears on its own is the one thing you are not
                        told about unless you ask for it when you add or edit a subscription.
                    </p>
                    <p>
                        To send alerts to an address that doesn't have a login here — a shared inbox or an on-call alias — use
                        the <strong>Email Recipients</strong> tab instead.
                    </p>
                </SectionIntro>
                <SortableTable
                    initialSortKey="createdAtUtc"
                    initialSortDir="desc"
                    isLoading={subscriptionHelpers.isLoading}
                    emptyValue={<p className="m-5">You have no alert subscriptions yet</p>}
                    columns={[
                        {
                            header: "Scope",
                            width: 200,
                            sortable: false,
                            renderFunc: (s) => (s ? scopeLabel(s) : ""),
                        },
                        {
                            header: "Notify Me About",
                            width: 150,
                            dataKey: "minSeverity",
                            renderFunc: (s) => minSeverityLabel(s?.minSeverity),
                        },
                        {
                            header: "Channels",
                            width: 150,
                            dataKey: "channels",
                            renderFunc: (s) => channelsLabel(s?.channels),
                        },
                        {
                            header: "Delivery",
                            width: 120,
                            dataKey: "digestMode",
                            renderFunc: (s) => (
                                <>
                                    <div>{digestModeLabel(s?.digestMode)}</div>
                                    {/* Worth showing in the row: it is off by default, so someone wondering why they
                                        never hear about a recovery should not have to open the editor to find out. */}
                                    {s?.notifyOnAutoResolve && (
                                        <div className="text-xs text-muted-body-text mt-1">Includes self-clearing alerts</div>
                                    )}
                                </>
                            ),
                        },
                        {
                            header: "Created",
                            width: 180,
                            dataKey: "createdAtUtc",
                            renderFunc: (s) => zonedTime(s?.createdAtUtc),
                        },
                        {
                            header: "",
                            width: 25,
                            renderFunc: (s) => (
                                <DropDownMenu>
                                    <IconLink Icon={Icons.Edit} onClick={() => handleEditOnClick(s)}>
                                        Edit
                                    </IconLink>
                                    <IconLink Icon={Icons.Trash} onClick={() => handleDeleteOnClick(s)}>
                                        Remove
                                    </IconLink>
                                </DropDownMenu>
                            ),
                        },
                    ]}
                    data={subscriptions}
                    pagination
                    defaultPageSize={10}
                    pageSizeOptions={[5, 10, 25, 50]}
                />
            </PageContainer>
            {confirmDialog.renderDialog()}
            {subscriptionDialog.renderDialog((model, helpers) => (
                <>
                    <AlertScopeInputs organizationId={organizationId} scope={model} allowDevice onChange={(scope) => applyScope(model, helpers, scope)} />
                    <InputRow>
                        <Select
                            label="Minimum Severity"
                            options={minSeverityOptions}
                            value={model.minSeverity}
                            onChange={(e) => helpers.updateModel("minSeverity", Number(e.target.value) as AlertSeverity)}
                        />
                    </InputRow>
                    <InputRow>
                        <InputLabel>Channels</InputLabel>
                        <FlagEnumCheckboxInput
                            enumType={AlertChannel}
                            keyFilter={(value) => value !== AlertChannel.None && value !== AlertChannel.Webhook}
                            nameToDisplayConverter={(key) => enumLabel(key)}
                            value={model.channels}
                            onChange={(value) => helpers.updateModel("channels", value ?? AlertChannel.None)}
                        />
                        {!model.channels && <div className="text-red-600 text-sm">Pick at least one channel, or the subscription notifies nobody.</div>}
                    </InputRow>
                    <InputRow>
                        <EnumSelect
                            label="Delivery"
                            enumType={AlertDigestMode}
                            nameToDisplayConverter={(key) => enumLabel(key)}
                            value={model.digestMode}
                            onChange={(value) => helpers.updateModel("digestMode", (value ?? AlertDigestMode.Immediate) as AlertDigestMode)}
                        />
                    </InputRow>
                    <InputRow>
                        <CheckboxInput
                            label="Tell me when an alert clears on its own"
                            checked={model.notifyOnAutoResolve ?? false}
                            onChange={(e) => helpers.updateModel("notifyOnAutoResolve", e.target.checked)}
                        />
                        <div className="text-gray-500 text-sm">
                            Off by default. An alert that clears on its own means the device stopped reporting the problem — nobody
                            did anything and nothing needs doing, and it used to be half of all the alert email you received. You are
                            always told when a person resolves one.
                        </div>
                    </InputRow>
                </>
            ))}
        </>
    );
};
